/**
 * Background-tab "keep alive" via a sub-perceptible audio signal on the
 * top-level page.
 *
 * Why this is non-trivial:
 *   Chrome's "tab is audible" heuristic does NOT inspect HTMLAudioElement.volume.
 *   It samples the actual audio output energy reaching the speakers. A
 *   silent-WAV-at-volume-0 (our previous implementation) therefore registers
 *   as a SILENT tab — meaning intensive throttling still kicks in after 5+
 *   minutes hidden AND new media playback triggered while hidden can be
 *   suppressed/muted by the browser. The result the user observed: at the
 *   crossfade point the track-name updates and the iframe's getCurrentTime()
 *   starts advancing (video state = PLAYING), but no sound reaches the user
 *   until the tab is foregrounded.
 *
 * Approach (Web Audio API):
 *   - Create a single shared AudioContext (lazy, gesture-resumed if needed).
 *   - A 20 Hz sine wave (well below the ~50 Hz floor of typical playback
 *     systems and ~10× below the threshold of human hearing for low-end
 *     monitors) connected through a GainNode at gain = 0.0008 — quiet enough
 *     that no human or speaker can perceive it, loud enough that Chrome's
 *     audibility detector registers measurable output energy and treats the
 *     tab as audible.
 *   - The oscillator runs continuously while playback is requested; gain
 *     drops to 0 (effectively muted) when not.
 *
 * Ref-counted singleton — multiple callers can request keep-alive without
 * conflicting with each other.
 */

let _ctx = null;
let _osc = null;
let _gain = null;
let _started = false;
let _refCount = 0;

const ACTIVE_GAIN = 0.005; // bumped from 0.0008 — still inaudible on virtually all hardware
                            // but well above Chrome's audibility detection threshold (~-50 dBFS)
const FREQUENCY_HZ = 50;    // moved up from 20 Hz — many onboard speakers can't reproduce 20 Hz
                            // at all, which makes the signal harder for Chrome to detect

function _ensureContext() {
  if (_ctx) return _ctx;
  const Ctor = window.AudioContext || window.webkitAudioContext;
  if (!Ctor) return null;
  try {
    _ctx = new Ctor();
    _osc = _ctx.createOscillator();
    _gain = _ctx.createGain();
    _osc.type = 'sine';
    _osc.frequency.value = FREQUENCY_HZ;
    _gain.gain.value = 0; // start muted
    _osc.connect(_gain).connect(_ctx.destination);
    _osc.start();
  } catch (err) {
    if (process.env.NODE_ENV !== 'production') {
      console.debug('[keepAlive] AudioContext init failed:', err?.message || err);
    }
    _ctx = null;
  }
  return _ctx;
}

/**
 * Request the keep-alive loop to be running. Reference-counted so the loop
 * survives multiple callers requesting it. Must be invoked from a user-gesture
 * stack at least once for Chrome to permit the AudioContext to start
 * (resume()) — in practice the DJ Console's first Play click satisfies this.
 */
export function startKeepAlive() {
  _refCount++;
  const ctx = _ensureContext();
  if (!ctx) return;
  // Chrome creates the AudioContext in "suspended" state if no user gesture
  // has occurred yet. Calling resume() inside a click handler unlocks it.
  if (ctx.state === 'suspended') {
    ctx.resume().catch((err) => {
      if (process.env.NODE_ENV !== 'production') {
        console.debug('[keepAlive] resume deferred (no user gesture yet):', err?.message || err);
      }
    });
  }
  if (_started) return;
  if (_gain) {
    try { _gain.gain.value = ACTIVE_GAIN; } catch { /* noop */ }
  }
  _started = true;
}

/** Drop one reference. The loop only goes silent once the last caller releases. */
export function stopKeepAlive() {
  _refCount = Math.max(0, _refCount - 1);
  if (_refCount > 0) return;
  if (!_started) return;
  if (_gain) {
    try { _gain.gain.value = 0; } catch { /* noop */ }
  }
  _started = false;
}
